import {
  faClock,
  faPause,
  faPlay,
  faStop,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import React, { FC, MouseEvent, useMemo, useState } from "react";

import { WorkDetailsModel } from "../../../../../../../factories/work/model/work-details.model";
import { WorkTaskModel } from "../../../../../../../factories/work/model/work-task.model";
import AddTimer from "../../../../../../main/AddTimer";
import {
  canStartTimerForTarget,
  formatTimerHoursMinutes,
  formatTimerHoursMinutesSeconds,
  getWorkTaskBudgetSeconds,
  getWorkTaskTrackedSeconds,
  isTimerForTarget,
  isTimerPaused,
  isTimerRunning,
  RUNNING_TIMER_COLOR,
  TimerTarget,
} from "../../../../../../main/timer/timerTarget";
import { useTimerTarget } from "../../../../../../main/timer/useTimerTarget";

type Props = {
  work: WorkDetailsModel;
  workTask: WorkTaskModel;
};

export const WorkTaskTimer: FC<Props> = ({ work, workTask }) => {
  const {
    timer,
    duration,
    startWorkTaskTimer,
    pauseActiveTimer,
    toggleTimerForTarget,
  } = useTimerTarget();

  const [isConfirmTimeOpen, setIsConfirmTimeOpen] = useState(false);

  const target = useMemo<TimerTarget>(
    () => ({
      type: "workTask",
      workId: work.id,
      jobTaskId: workTask.id,
    }),
    [work.id, workTask.id],
  );

  const isCurrentTimer = isTimerForTarget(timer, target);
  const isRunning = isCurrentTimer && isTimerRunning(timer);
  const isPaused = isCurrentTimer && isTimerPaused(timer);
  const canStart = canStartTimerForTarget(timer, target);

  const trackedSeconds = getWorkTaskTrackedSeconds(workTask.duration);
  const budgetSeconds = getWorkTaskBudgetSeconds(workTask.timeBudget);
  const displayedTrackedSeconds = isCurrentTimer ? duration : trackedSeconds;
  const displayedTrackedTime = isCurrentTimer
    ? formatTimerHoursMinutesSeconds(displayedTrackedSeconds)
    : formatTimerHoursMinutes(displayedTrackedSeconds);

  const onStartOrToggle = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    if (!canStart) {
      return;
    }

    toggleTimerForTarget(target, () => startWorkTaskTimer({ work, workTask }));
  };

  const onStop = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    pauseActiveTimer();
    setIsConfirmTimeOpen(true);
  };

  return (
    <div className={"flex items-center gap-3 text-start text-xs font-semibold"}>
      <button
        type={"button"}
        onClick={onStartOrToggle}
        disabled={!canStart}
        className={classNames(
          "flex h-6 min-w-[58px] items-center justify-end gap-1 rounded border border-transparent px-1 text-[#1D2C3B] disabled:cursor-not-allowed disabled:opacity-50",
          isCurrentTimer &&
            "h-8 min-w-[92px] justify-center gap-2 rounded-[7px] border-[#E2DBFF] bg-[#F4F2FF] px-2 font-bold text-[#6B4EFF]",
          canStart &&
            (isCurrentTimer
              ? "cursor-pointer hover:bg-[#EDE8FF]"
              : "cursor-pointer hover:bg-[#4C637B0D]"),
        )}
        style={{ color: isRunning ? RUNNING_TIMER_COLOR : undefined }}
        title={"Subtask timer"}>
        <FontAwesomeIcon
          icon={faClock}
          className={classNames(isCurrentTimer ? "text-sm" : "text-[11px]")}
        />
        <span
          className={
            isCurrentTimer ? "[font-variant-numeric:tabular-nums]" : ""
          }>
          {displayedTrackedTime}
        </span>
      </button>

      {(isRunning || isPaused) && (
        <div className={"flex items-center gap-2"}>
          <button
            type={"button"}
            onClick={onStartOrToggle}
            className={
              "flex h-5 w-5 items-center justify-center rounded text-[10px] text-[#6E3BE9] hover:bg-[#6E3BE91A]"
            }
            title={isPaused ? "Continue timer" : "Pause timer"}>
            <FontAwesomeIcon icon={isPaused ? faPlay : faPause} />
          </button>
          <button
            type={"button"}
            onClick={onStop}
            className={
              "flex h-5 w-5 items-center justify-center rounded text-[10px] text-red-500 hover:bg-red-50"
            }
            title={"Stop timer"}>
            <FontAwesomeIcon icon={faStop} />
          </button>
        </div>
      )}

      <div className={"min-w-[36px] text-[#9FA1AD]"}>
        {formatTimerHoursMinutes(budgetSeconds)}
      </div>

      {isConfirmTimeOpen && (
        <AddTimer
          isOpen={isConfirmTimeOpen}
          closeModal={() => setIsConfirmTimeOpen(false)}
        />
      )}
    </div>
  );
};
